import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { AlertTriangle, RefreshCw } from 'lucide-react';

/**
 * Generic React error boundary. Wrap any subtree that you'd rather have
 * render a recoverable fallback instead of unmounting the entire app
 * (which Reactdoes by default on uncaught render errors).
 *
 * Usage:
 *   <ErrorBoundary label="Lead Detail">
 *     <RiskyComponent />
 *   </ErrorBoundary>
 */
export class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, info: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // Surface the error to the console + a structured log so engineers
    // can pick it up from monitoring without losing the user.
    // eslint-disable-next-line no-console
    console.error(
      `[ErrorBoundary${this.props.label ? ` · ${this.props.label}` : ''}]`,
      error, info?.componentStack
    );
    this.setState({ info });
    if (typeof this.props.onError === 'function') {
      try { this.props.onError(error, info); } catch (_) { /* noop */ }
    }
  }

  reset = () => this.setState({ hasError: false, error: null, info: null });

  render() {
    if (!this.state.hasError) return this.props.children;
    if (this.props.fallback) {
      return typeof this.props.fallback === 'function'
        ? this.props.fallback({ error: this.state.error, reset: this.reset })
        : this.props.fallback;
    }
    return (
      <Card className="border-red-500/40 bg-red-500/5" data-testid="error-boundary-fallback">
        <CardContent className="py-6 space-y-3">
          <div className="flex items-center gap-2 text-red-600 font-semibold">
            <AlertTriangle className="h-5 w-5" />
            Something went wrong{this.props.label ? ` in ${this.props.label}` : ''}
          </div>
          <p className="text-sm text-muted-foreground">
            The rest of the app is still working — only this section failed
            to render. Try the action below, or refresh the page.
          </p>
          {this.state.error?.message && (
            <pre className="text-xs bg-card border rounded p-2 max-h-32 overflow-auto whitespace-pre-wrap break-words text-red-700">
              {String(this.state.error?.message || this.state.error)}
            </pre>
          )}
          <Button size="sm" variant="outline" onClick={this.reset} data-testid="error-boundary-retry">
            <RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Retry
          </Button>
        </CardContent>
      </Card>
    );
  }
}

export default ErrorBoundary;
